import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'

interface HealthCheck {
  endpoint: string
  status: 'pending' | 'pass' | 'fail'
  message: string
  timestamp?: string
  responseTime?: number
  buildSha?: string
}

@customElement('health-page')
export class HealthPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      max-width: 800px;
      margin: 2rem auto;
      padding: 2rem;
      font-family: system-ui, -apple-system, sans-serif;
    }

    h1 {
      color: #213547;
      margin-bottom: 1rem;
    }

    .summary {
      display: flex;
      gap: 1rem;
      margin-bottom: 2rem;
      padding: 1rem;
      background: #f8f9fa;
      border-radius: 8px;
    }

    .summary-item {
      flex: 1;
      text-align: center;
    }

    .summary-label {
      font-size: 0.875rem;
      color: #666;
      margin-bottom: 0.25rem;
    }

    .summary-value {
      font-size: 1.5rem;
      font-weight: bold;
    }

    .summary-value.pass {
      color: #00b894;
    }

    .summary-value.fail {
      color: #d63031;
    }

    .summary-value.pending {
      color: #fdcb6e;
    }

    .checks {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .check-card {
      padding: 1rem;
      border: 1px solid #ddd;
      border-radius: 8px;
      background: white;
    }

    .check-card.pass {
      border-left: 4px solid #00b894;
    }

    .check-card.fail {
      border-left: 4px solid #d63031;
    }

    .check-card.pending {
      border-left: 4px solid #fdcb6e;
    }

    .check-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .check-endpoint {
      font-family: monospace;
      font-weight: 500;
    }

    .check-status {
      padding: 0.25rem 0.75rem;
      border-radius: 4px;
      font-size: 0.875rem;
      font-weight: 500;
      text-transform: uppercase;
    }

    .check-status.pass {
      background: #00b894;
      color: white;
    }

    .check-status.fail {
      background: #d63031;
      color: white;
    }

    .check-status.pending {
      background: #fdcb6e;
      color: #333;
    }

    .check-details {
      font-size: 0.875rem;
      color: #666;
    }

    .check-message {
      margin-top: 0.5rem;
    }

    .api-origin {
      margin-top: 2rem;
      padding: 1rem;
      background: #f0f0f0;
      border-radius: 4px;
      font-size: 0.875rem;
    }

    .api-origin code {
      font-family: monospace;
      background: white;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
    }
  `

  @state()
  private checks: HealthCheck[] = [
    { endpoint: '/health', status: 'pending', message: 'Checking...' },
    { endpoint: '/db-health', status: 'pending', message: 'Checking...' }
  ]

  private apiBaseUrl = import.meta.env.VITE_API_ORIGIN

  connectedCallback() {
    super.connectedCallback()
    this.runChecks()
  }

  private async runChecks() {
    for (let i = 0; i < this.checks.length; i++) {
      await this.runCheck(i)
    }
  }

  private async runCheck(index: number) {
    const check = this.checks[index]
    const startTime = Date.now()

    try {
      const response = await fetch(`${this.apiBaseUrl}${check.endpoint}`)
      const responseTime = Date.now() - startTime

      if (!response.ok) {
        this.checks[index] = {
          ...check,
          status: 'fail',
          message: `HTTP ${response.status}: ${response.statusText}`,
          timestamp: new Date().toISOString(),
          responseTime
        }
        this.requestUpdate()
        return
      }

      const contentType = response.headers.get('content-type')
      let data: any

      if (contentType?.includes('application/json')) {
        data = await response.json()
      } else {
        data = await response.text()
      }

      // Extract build_sha if present (from /health endpoint)
      const buildSha = data?.build_sha

      // Validate the response based on endpoint
      let isValid = true
      let validationMessage = ''

      if (check.endpoint === '/health') {
        // Health endpoint should return { status: "healthy", ... }
        if (!data?.status || data.status !== 'healthy') {
          isValid = false
          validationMessage = `Expected status="healthy", got: ${data?.status || 'undefined'}`
        }
      } else if (check.endpoint === '/db-health') {
        // DB health endpoint should return { status: "healthy", database: "<db_type>", ... }
        if (!data?.status || data.status !== 'healthy') {
          isValid = false
          validationMessage = `Expected status="healthy", got: ${data?.status || 'undefined'}`
        }
        // Just verify database field exists (can be "postgres", "connected", etc.)
        if (!data?.database) {
          isValid = false
          validationMessage += ` | Expected database field to be present, got: undefined`
        }
      }

      this.checks[index] = {
        ...check,
        status: isValid ? 'pass' : 'fail',
        message: isValid 
          ? (typeof data === 'string' ? data : JSON.stringify(data, null, 2))
          : `Validation failed: ${validationMessage}\nResponse: ${JSON.stringify(data, null, 2)}`,
        timestamp: new Date().toISOString(),
        responseTime,
        buildSha
      }
    } catch (error) {
      const responseTime = Date.now() - startTime
      this.checks[index] = {
        ...check,
        status: 'fail',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
        responseTime
      }
    }

    this.requestUpdate()
  }

  private get passCount() {
    return this.checks.filter(c => c.status === 'pass').length
  }

  private get failCount() {
    return this.checks.filter(c => c.status === 'fail').length
  }

  private get pendingCount() {
    return this.checks.filter(c => c.status === 'pending').length
  }

  private get overallStatus() {
    if (this.pendingCount > 0) return 'pending'
    if (this.failCount > 0) return 'fail'
    return 'pass'
  }

  render() {
    return html`
      <h1>🩺 Finance Suite - PoC - Health Checks</h1>

      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Status</div>
          <div
            class="summary-value ${this.overallStatus}"
            data-testid="health-summary-status"
            data-status=${this.overallStatus}
          >
            ${this.overallStatus === 'pass' ? '✓ OK' : 
              this.overallStatus === 'fail' ? '✗ FAIL' : '⋯ RUNNING'}
          </div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Passed</div>
          <div class="summary-value pass">${this.passCount}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Failed</div>
          <div class="summary-value fail">${this.failCount}</div>
        </div>
      </div>

      <div class="checks">
        ${this.checks.map(check => html`
          <div
            class="check-card ${check.status}"
            data-testid="health-check-card"
            data-endpoint=${check.endpoint}
            data-status=${check.status}
          >
            <div class="check-header">
              <span class="check-endpoint">${check.endpoint}</span>
              <span class="check-status ${check.status}" data-testid="health-check-status">${check.status}</span>
            </div>
            <div class="check-details">
              ${check.responseTime !== undefined 
                ? html`<div>Response time: ${check.responseTime}ms</div>` 
                : ''}
              ${check.timestamp 
                ? html`<div>Timestamp: ${check.timestamp}</div>` 
                : ''}
              ${check.buildSha 
                ? html`<div>API Build SHA: <code>${check.buildSha}</code></div>` 
                : ''}
            </div>
            <div class="check-message">
              <pre
                style="margin: 0; white-space: pre-wrap; word-break: break-word;"
                data-testid="health-check-message"
              >${check.message}</pre>
            </div>
          </div>
        `)}
      </div>

      <div class="api-origin">
        <strong>API Origin:</strong> <code>${this.apiBaseUrl}</code><br>
        <strong>Web Build SHA:</strong> <code>${import.meta.env.VITE_BUILD_SHA || 'dev'}</code>
      </div>
    `
  }
}
